import { useEffect, useState } from "react";
import { salesInvoiceApi } from "../../../api/salesInvoice";
import { unwrapResponse } from "../../../api/client";

export interface ReturnableInvoice {
  id: number;
  posOrderId?: number;
  invoiceNumber?: string;
  referenceNumber?: string;
  date?: string;
  grandTotal?: number;
  customerId?: number;
  customer?: { id?: number; name?: string; membershipId?: string | null };
  partyProfile?: { id?: number; name?: string; phoneNumber?: string; emailAddress?: string; memberShipId?: string | null; membershipId?: string | null };
  membershipId?: string | null;
  salesPerson?: { id?: number; name?: string };
  customerName?: string;
  lines?: ReturnableInvoiceLine[];
  posOrderLines?: ReturnableInvoiceLine[];
  orderLines?: ReturnableInvoiceLine[];
}

export interface ReturnableInvoiceLine {
  itemId?: number;
  itemName?: string;
  name?: string;
  itemCode?: string;
  code?: string;
  quantity?: number;
  qty?: number;
  price?: number;
  amount?: number;
  itemTypeId?: number;
  isSerialized?: boolean;
  serialized?: boolean;
  taxId?: number | null;
  discount?: number;
  tax?: number;
  warehouseId?: number;
  binId?: number;
  isPromoItem?: boolean;
}

interface ExchangeModalProps {
  onClose: () => void;
  onInvoiceSelected: (invoice: ReturnableInvoice) => Promise<void>;
}

const DEFAULT_PAYLOAD = {
  query: {
    draw: 1,
    start: 0,
    length: 15,
    search: { value: "", regex: false },
  },
  customerId: null,
  referenceId: null,
  fromDate: null,
  todate: null,
  invoiceStatusId: [0],
  fetchExpired: false,
  validityDays: 7,
};

export default function ExchangeModal({ onClose, onInvoiceSelected }: ExchangeModalProps) {
  const [invoices, setInvoices] = useState<ReturnableInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);

  const loadInvoices = async (search = "") => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        ...DEFAULT_PAYLOAD,
        query: {
          ...DEFAULT_PAYLOAD.query,
          search: { value: search, regex: false },
        },
      };
      const res = await salesInvoiceApi.getReturnablePosInvoices(payload);
      const data = unwrapResponse<any>(res);
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setInvoices(list);
    } catch (err: any) {
      setError(err?.message || "Failed to load returnable invoices.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const handleSearch = () => loadInvoices(searchValue);

  const handleSelect = async (invoice: ReturnableInvoice) => {
    setSelectingId(invoice.id);
    setSelectError(null);
    try {
      await onInvoiceSelected(invoice);
      onClose();
    } catch (err: any) {
      const msg = err?.message || "Failed to load invoice details. Please try again.";
      if (msg) setSelectError(msg);
      setSelectingId(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
  };

  const getCustomerName = (inv: ReturnableInvoice) =>
    inv?.partyProfile?.name ?? inv?.customer?.name ?? inv?.customerName ?? "Unknown";

  const getCustomerCode = (inv: ReturnableInvoice) =>
    inv?.partyProfile?.memberShipId ?? inv?.partyProfile?.membershipId ?? inv?.customer?.membershipId ?? inv?.membershipId ?? null;

  const getInvoiceNumber = (inv: ReturnableInvoice) =>
    inv?.invoiceNumber ?? inv?.referenceNumber ?? `#${inv.id}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] min-h-[300px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-teal-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Return</h2>
            <p className="text-sm text-teal-100 whitespace-nowrap">Select a returnable invoice</p>
          </div>
          <button
            onClick={onClose}
            disabled={selectingId !== null}
            className="w-8 h-8 flex items-center justify-center hover:bg-teal-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadInvoices(e.currentTarget.value)}
              placeholder="Scan or enter product code / invoice number"
              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
              autoFocus
            />
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-sm transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              {isLoading
                ? <><i className="ri-loader-4-line animate-spin mr-1"></i>Searching...</>
                : <><i className="ri-search-line mr-1"></i>Search</>
              }
            </button>
          </div>
        </div>

        {/* Invoice List */}
        <div className="flex-1 overflow-y-auto p-6">
          {(error || selectError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {selectError ?? error}
            </div>
          )}

          {isLoading && !invoices.length ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <div className="text-center">
                <i className="ri-loader-4-line text-5xl animate-spin mb-3"></i>
                <p className="text-sm">Loading invoices...</p>
              </div>
            </div>
          ) : !invoices.length ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <div className="text-center">
                <i className="ri-file-list-3-line text-5xl mb-3"></i>
                <p className="text-sm">No returnable invoices found</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {invoices.map((invoice) => {
                const isThisSelecting = selectingId === invoice.id;
                const isOtherSelecting = selectingId !== null && selectingId !== invoice.id;
                return (
                  <button
                    key={invoice.id}
                    onClick={() => handleSelect(invoice)}
                    disabled={selectingId !== null}
                    className={`w-full text-left border-2 rounded-lg p-4 transition-all ${
                      isThisSelecting
                        ? 'border-teal-500 bg-teal-50 cursor-wait'
                        : isOtherSelecting
                        ? 'border-gray-200 bg-white opacity-50 cursor-not-allowed'
                        : 'border-gray-200 bg-white hover:border-teal-400 hover:bg-teal-50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${isThisSelecting ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-600'}`}>
                          <i className={`text-xl ${isThisSelecting ? 'ri-loader-4-line animate-spin' : 'ri-receipt-line'}`}></i>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 whitespace-nowrap">{getInvoiceNumber(invoice)}</p>
                          <p className="text-sm text-gray-600 whitespace-nowrap">
                            {getCustomerName(invoice)}
                            {getCustomerCode(invoice) && <>&nbsp;·&nbsp;{getCustomerCode(invoice)}</>}
                          </p>
                          <p className="text-xs text-gray-400 whitespace-nowrap">{formatDate(invoice.date)}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                          {Number(invoice.grandTotal ?? 0).toFixed(2)}
                        </p>
                        <p className={`text-xs font-semibold whitespace-nowrap ${isThisSelecting ? 'text-teal-700' : 'text-teal-600'}`}>
                          {isThisSelecting ? 'Loading...' : 'Select →'}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            disabled={selectingId !== null}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
